/**
 * Shared layout constants for chip input popups
 *
 * Used by BuyInPopup and RebuyPopup to ensure consistent spacing
 * and dimensions for their shared UI elements.
 *
 * @see BuyInPopup - Initial buy-in when sitting at table
 * @see RebuyPopup - Rebuy when player loses all chips
 */

// =============================================================================
// VERTICAL GAPS
// =============================================================================

/**
 * Vertical gaps between shared elements.
 * These ensure consistent spacing in the shared section:
 * Amount → Slider → Labels → Checkboxes → Buttons
 */
export const CHIP_INPUT_GAPS = {
  /** Gap from amount display to slider */
  amountToSlider: 45,

  /** Gap from slider to min/max labels */
  sliderToLabels: 18,

  /** Gap from labels to checkboxes */
  labelsToCheckboxes: 37,

  /** Gap from checkboxes to buttons */
  checkboxesToButtons: 45,
};

// =============================================================================
// SLIDER
// =============================================================================

/** Slider dimensions and colors */
export const SLIDER_LAYOUT = {
  width: 300,
  height: 6,
  handleSize: 22,
  trackColor: 0x2a2a4e,
  fillColor: 0x4caf50,
  handleColor: 0xffffff,
};

// =============================================================================
// CHECKBOXES
// =============================================================================

/** Checkbox layout (horizontal, centered) */
export const CHECKBOX_LAYOUT = {
  size: 18,
  gap: 35,
  itemWidth: 95,
  uncheckedColor: 0x2a2a4e,
  checkedColor: 0x4caf50,
  checkmarkColor: 0xffffff,
  labelColor: 0xaaaaaa,
  fontSize: 13,
};

// =============================================================================
// BUTTONS
// =============================================================================

/** Button dimensions */
export const BUTTON_LAYOUT = {
  width: 130,
  height: 44,
  gap: 16,
};

// =============================================================================
// POPUP SIZING
// =============================================================================

/** Standard popup padding */
export const POPUP_LAYOUT = {
  sidePadding: 40,
  verticalPadding: 40,
};

/**
 * Calculate popup dimensions dynamically based on content bounds.
 *
 * @param topOffset - Absolute Y distance from center to topmost element (e.g., 130)
 * @param bottomOffset - Y distance from center to bottom edge of lowest element (e.g., buttonY + buttonHeight)
 * @returns Calculated width and height for popup background
 */
export function calculatePopupSize(
  topOffset: number,
  bottomOffset: number
): { width: number; height: number } {
  return {
    width: SLIDER_LAYOUT.width + POPUP_LAYOUT.sidePadding * 2,
    height: topOffset + bottomOffset + POPUP_LAYOUT.verticalPadding * 2,
  };
}
